import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../../context/AppContext";
import "../../../../assests/css/admin/AdminContactForm/AdminContactForm.css";

const STATUS_OPTIONS = [
  { value: "new",      label: "New",      icon: "🟡" },
  { value: "opened",   label: "Opened",   icon: "🟣" },
  { value: "replied",  label: "Replied",  icon: "🟢" },
  { value: "resolved", label: "Resolved", icon: "🔵" },
  { value: "closed",   label: "Closed",   icon: "⚫" },
];

const getInitials = (name = "") =>
  name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const AdminContactForm = () => {
  const { baseUrl } = useContext(AppContext);

  const [records, setRecords]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [search, setSearch]                 = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [replyText, setReplyText]           = useState("");
  const [sendingEmail, setSendingEmail]     = useState(false);
  const [showCompose, setShowCompose]       = useState(false);

  // ── FETCH DATA FROM API ───────────────────────────────────────────────────────
  const fetchRecords = () => {
    setLoading(true);
    axios.get(`${baseUrl}/admin/contacts/viewAllContacts`)
      .then(({ data }) => {
        if (data.success) {
          setRecords(data.contacts || []);
        } else {
          setRecords([]);
        }
        setLoading(false);
      })
      .catch(() => {
        setRecords([]);
        setLoading(false);
      });
  };

  useEffect(() => { fetchRecords(); }, []);

  // ── STATS ─────────────────────────────────────────────────────────────────────
  const total    = records.length;
  const newCount = records.filter((r) => (r.status || "new") === "new").length;
  const resolved = records.filter((r) => r.status === "resolved").length;
  const replied  = records.filter((r) => r.status === "replied").length;

  // ── FILTERED RECORDS ──────────────────────────────────────────────────────────
  const filtered = records.filter((r) => {
    const matchSearch =
      (r.name    || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.email   || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.subject || "").toLowerCase().includes(search.toLowerCase());
    return matchSearch;
  });

  // ── UPDATE STATUS ─────────────────────────────────────────────────────────────
  const updateStatus = (id, status) => {
    setRecords((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    if (selectedRecord && selectedRecord.id === id) {
      setSelectedRecord((prev) => ({ ...prev, status }));
    }
  };

  // ── SEND EMAIL ────────────────────────────────────────────────────────────────
  const sendEmail = () => {
    if (!replyText.trim()) {
      Swal.fire({ icon: "warning", title: "Empty!", text: "Please write a reply first." });
      return;
    }
    setSendingEmail(true);
    setTimeout(() => {
      setSendingEmail(false);
      setReplyText("");
      setShowCompose(false);
      updateStatus(selectedRecord.id, "replied");
      Swal.fire({ icon: "success", title: "Email Sent!", text: "Reply sent successfully.", timer: 1800, showConfirmButton: false });
    }, 1000);
  };

  // ── OPEN DETAIL ───────────────────────────────────────────────────────────────
  const openDetail = (record) => {
    setSelectedRecord(record);
    setReplyText("");
    setShowCompose(false);
    if ((record.status || "new") === "new") {
      updateStatus(record.id, "opened");
    }
  };

  return (
    <div className="Contact-Handle-Admiin-wrapper">

      {/* HEADER */}
      <div className="Contact-Handle-Admiin-header">
        <h1>Contact Inquiries</h1>
        <p>Manage and respond to customer messages</p>
      </div>

      {/* STATS */}
      <div className="Contact-Handle-Admiin-stats">
        <div className="Contact-Handle-Admiin-stat-card">
          <div className="Contact-Handle-Admiin-stat-label">Total Inquiries</div>
          <div className="Contact-Handle-Admiin-stat-value purple">{total}</div>
          <div className="Contact-Handle-Admiin-stat-sub up">↑ All time</div>
        </div>
        <div className="Contact-Handle-Admiin-stat-card">
          <div className="Contact-Handle-Admiin-stat-label">New Messages</div>
          <div className="Contact-Handle-Admiin-stat-value orange">{newCount}</div>
          <div className="Contact-Handle-Admiin-stat-sub">Requiring response</div>
        </div>
        <div className="Contact-Handle-Admiin-stat-card">
          <div className="Contact-Handle-Admiin-stat-label">Replied</div>
          <div className="Contact-Handle-Admiin-stat-value">{replied}</div>
          <div className="Contact-Handle-Admiin-stat-sub">Responses sent</div>
        </div>
        <div className="Contact-Handle-Admiin-stat-card">
          <div className="Contact-Handle-Admiin-stat-label">Resolved</div>
          <div className="Contact-Handle-Admiin-stat-value">{resolved}</div>
          <div className="Contact-Handle-Admiin-stat-sub">Past 30 days</div>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="Contact-Handle-Admiin-filterbar">
        <div className="Contact-Handle-Admiin-search">
          <span className="Contact-Handle-Admiin-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by name, email or subject..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* TABLE */}
      <div className="Contact-Handle-Admiin-table-container">
        <table className="Contact-Handle-Admiin-table">
          <thead>
            <tr>
              <th>Customer Name</th>
              <th>Subject</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
                  Loading...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="Contact-Handle-Admiin-empty">
                    <div className="Contact-Handle-Admiin-empty-icon">📭</div>
                    <div>No records found</div>
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((record) => (
                <tr key={record.id} onClick={() => openDetail(record)}>
                  <td>
                    <div className="Contact-Handle-Admiin-customer">
                      <div className="Contact-Handle-Admiin-avatar">{getInitials(record.name)}</div>
                      <div>
                        <div className="Contact-Handle-Admiin-customer-name">{record.name}</div>
                        <div className="Contact-Handle-Admiin-customer-email">{record.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="Contact-Handle-Admiin-subject-title">{record.subject}</div>
                    <div className="Contact-Handle-Admiin-subject-preview">{record.message}</div>
                  </td>
                  <td>
                    <div className="Contact-Handle-Admiin-date">{formatDate(record.created_at)}</div>
                  </td>
                  <td>
                    <span className={`Contact-Handle-Admiin-badge ${record.status || "new"}`}>
                      <span className="Contact-Handle-Admiin-badge-dot"></span>
                      {(record.status || "new").toUpperCase()}
                    </span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      className="Contact-Handle-Admiin-actions-btn"
                      onClick={() => { openDetail(record); setShowCompose(true); }}
                    >
                      ✉️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* DETAIL MODAL */}
      {selectedRecord && (
        <div className="Contact-Handle-Admiin-modal-overlay" onClick={() => setSelectedRecord(null)}>
          <div className="Contact-Handle-Admiin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="Contact-Handle-Admiin-modal-header">
              <div className="Contact-Handle-Admiin-modal-title">Message Detail</div>
              <button className="Contact-Handle-Admiin-modal-close" onClick={() => setSelectedRecord(null)}>✕</button>
            </div>
            <div className="Contact-Handle-Admiin-modal-body">
              <div className="Contact-Handle-Admiin-modal-meta">
                <div className="Contact-Handle-Admiin-avatar" style={{ width: 48, height: 48, fontSize: 16 }}>
                  {getInitials(selectedRecord.name)}
                </div>
                <div className="Contact-Handle-Admiin-modal-meta-info">
                  <div className="Contact-Handle-Admiin-modal-meta-name">{selectedRecord.name}</div>
                  <div className="Contact-Handle-Admiin-modal-meta-email">{selectedRecord.email}</div>
                </div>
                <span className={`Contact-Handle-Admiin-badge ${selectedRecord.status || "new"}`}>
                  <span className="Contact-Handle-Admiin-badge-dot"></span>
                  {(selectedRecord.status || "new").toUpperCase()}
                </span>
              </div>

              <div className="Contact-Handle-Admiin-modal-subject">Subject</div>
              <div style={{ fontWeight: 600, marginBottom: 16, fontSize: 15 }}>{selectedRecord.subject}</div>

              <div className="Contact-Handle-Admiin-modal-subject">Full Message</div>
              <div className="Contact-Handle-Admiin-modal-message">{selectedRecord.message}</div>

              <div className="Contact-Handle-Admiin-modal-date">
                📅 Received: {formatDate(selectedRecord.created_at)}
              </div>

              {!showCompose && (
                <button
                  className="Contact-Handle-Admiin-btn primary"
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => setShowCompose(true)}
                >
                  ✉️ Send Email Reply
                </button>
              )}

              {showCompose && (
                <div className="Contact-Handle-Admiin-compose">
                  <label className="Contact-Handle-Admiin-compose-label">
                    Reply to {selectedRecord.email}
                  </label>
                  <textarea
                    placeholder="Write your reply here..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <div className="Contact-Handle-Admiin-modal-footer">
                    <button className="Contact-Handle-Admiin-btn secondary" onClick={() => setShowCompose(false)}>
                      Cancel
                    </button>
                    <button className="Contact-Handle-Admiin-btn primary" onClick={sendEmail} disabled={sendingEmail}>
                      {sendingEmail ? "Sending..." : "✉️ Send Reply"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminContactForm;